import React, { useState, useEffect } from "react";
import "./PWAInstallButton.css";

const PWAInstallButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  useEffect(() => {
    // Verificar si ya está instalado
    if (window.matchMedia("(display-mode: standalone)").matches) {
      setIsInstalled(true);
      return;
    }

    // Escuchar el evento beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);

      // Mostrar el modal automáticamente después de 5 segundos (solo la primera vez)
      if (!localStorage.getItem("pwaInstallModalShown")) {
        setTimeout(() => {
          setShowInstallModal(true);
          localStorage.setItem("pwaInstallModalShown", "true");
        }, 5000);
      }
    };

    // Escuchar cuando se instala
    const handleAppInstalled = () => {
      setShowInstallButton(false);
      setShowInstallModal(false);
      setDeferredPrompt(null);
      setIsInstalled(true);
      setIsInstalling(false);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      return;
    }

    setIsInstalling(true);

    // Mostrar el prompt de instalación
    deferredPrompt.prompt();

    // Esperar la respuesta del usuario
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      console.log("Usuario aceptó instalar la PWA");
    } else {
      console.log("Usuario rechazó instalar la PWA");
      setIsInstalling(false);
      setShowInstallModal(false);
    }

    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  const openModal = () => {
    setShowInstallModal(true);
  };

  const closeModal = () => {
    setShowInstallModal(false);
  };

  // No mostrar el botón si ya está instalado o no se puede instalar
  if (isInstalled || !showInstallButton) {
    return null;
  }

  return (
    <>
      {/* Botón flotante */}
      <div className="pwa-install-container">
        <button
          className="pwa-install-button"
          onClick={openModal}
          aria-label="Instalar aplicación"
        >
          <svg
            className="pwa-install-icon"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.979 19.804 4.587 19.412C4.195 19.02 3.999 18.549 3.999 17.999V15H5.999V18H18V15H20V17.999C20 18.549 19.804 19.02 19.412 19.412C19.02 19.804 18.549 20 17.999 20H6Z"
              fill="currentColor"
            />
          </svg>
          <span>Instalar App</span>
        </button>

        <div className="pwa-install-tooltip">
          ¡Instala nuestra app para acceso rápido!
        </div>
      </div>

      {/* Modal de instalación */}
      {showInstallModal && (
        <div className="pwa-modal-overlay" onClick={closeModal}>
          <div className="pwa-modal" onClick={(e) => e.stopPropagation()}>
            {/* Contenido del modal */}
            <div className="modal-header">
              <div className="modal-icon-wrapper">
                <div className="modal-icon-glow"></div>
                <img
                  src="/android-chrome-192x192.png"
                  alt="AI Voice Agent"
                  className="modal-app-icon"
                />
              </div>

              <button
                className="modal-close"
                onClick={closeModal}
                aria-label="Cerrar"
              >
                ×
              </button>
            </div>

            <div
              className="modal-content"
              style={{ width: "100%", borderRadius: "0px" }}
            >
              <h2 className="modal-title">
                Instala <span className="gradient-text">AI Voice Agent</span>
              </h2>

              <p className="modal-description">
                Accede instantáneamente a tu agente de voz IA desde tu
                dispositivo
              </p>

              <div className="modal-features">
                <div className="feature-item">
                  <div className="feature-icon">⚡</div>
                  <div className="feature-text">
                    <strong>Acceso Rápido</strong>
                    <span>Abre la app con un solo toque</span>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">🔔</div>
                  <div className="feature-text">
                    <strong>Notificaciones</strong>
                    <span>Mantente informado en tiempo real</span>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">📱</div>
                  <div className="feature-text">
                    <strong>Sin Conexión</strong>
                    <span>Funciona incluso offline</span>
                  </div>
                </div>

                <div className="feature-item">
                  <div className="feature-icon">🚀</div>
                  <div className="feature-text">
                    <strong>Rendimiento</strong>
                    <span>Más rápido que el navegador</span>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  className="modal-install-btn"
                  onClick={handleInstallClick}
                  disabled={isInstalling}
                >
                  {isInstalling ? (
                    <>
                      <span className="installing-spinner"></span>
                      Instalando...
                    </>
                  ) : (
                    <>
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 16L7 11L8.4 9.55L11 12.15V4H13V12.15L15.6 9.55L17 11L12 16ZM6 20C5.45 20 4.979 19.804 4.587 19.412C4.195 19.02 3.999 18.549 3.999 17.999V15H5.999V18H18V15H20V17.999C20 18.549 19.804 19.02 19.412 19.412C19.02 19.804 18.549 20 17.999 20H6Z"
                          fill="currentColor"
                        />
                      </svg>
                      Instalar Ahora
                    </>
                  )}
                </button>

                <button className="modal-cancel-btn" onClick={closeModal}>
                  Más Tarde
                </button>
              </div>

              <p className="modal-footer-text">
                Instalación gratuita • Solo 2MB • Sin registro
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallButton;
